import React, { useState } from 'react';
import { Users, Search, Pencil, Trash2, Mail, Phone } from 'lucide-react';
import { useData } from '../context/DataContext';
import { fmtRM } from '../lib/store';
import { PageHeader, EmptyState, Card } from '../components/Shared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const empty = { name: '', contact: '', email: '', phone: '', address: '', outstanding: 0 };

export default function Customers() {
  const { customers, addItem, updateItem, removeItem } = useData();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const filtered = customers.filter((c) =>
    [c.name, c.contact, c.email].join(' ').toLowerCase().includes(q.toLowerCase())
  );

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };

  const save = () => {
    if (!form.name.trim()) { toast.error('Customer name is required'); return; }
    if (editId) { updateItem('customers', editId, form); toast.success('Customer updated'); }
    else { addItem('customers', { ...form, outstanding: Number(form.outstanding) || 0 }); toast.success('Customer added'); }
    setOpen(false);
  };

  const del = (id) => { removeItem('customers', id); toast.success('Customer removed'); };

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Customers" subtitle="Store customer details, contacts and history." actionLabel="Add Customer" onAction={openNew} />

      <Card className="mb-4 p-3">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers..." className="pl-9" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No customers found" subtitle="Add your first customer to get started." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((c) => (
            <Card key={c.id} className="p-5 transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-700">
                    {c.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.contact}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(c)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(c.id)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</p>
                <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}</p>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-xs text-slate-500">Outstanding</span>
                <span className={`text-sm font-bold ${c.outstanding > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmtRM(c.outstanding)}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? 'Edit Customer' : 'Add Customer'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Company / Customer Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Contact Person</Label><Input className="mt-1.5" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Email</Label><Input className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Outstanding (RM)</Label><Input type="number" className="mt-1.5" value={form.outstanding} onChange={(e) => setForm({ ...form, outstanding: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save}>{editId ? 'Save Changes' : 'Add Customer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
